import React, { useEffect, useMemo, useState, useCallback } from 'react';

import {
	AccountCircleTwoTone,
	AdUnitsTwoTone,
	BusinessTwoTone,
	ExpandLess,
	ExpandMore,
	InsightsTwoTone,
	WalletTwoTone,
	SchoolTwoTone,
	SupervisorAccountTwoTone,
	FolderSharedTwoTone,
	VerifiedTwoTone,
	RouteTwoTone,
	Diversity3TwoTone,
	ViewSidebarTwoTone,
	TravelExplore,
	DiscountTwoTone,
	SupportAgentTwoTone,
	StorefrontTwoTone,
	FamilyRestroomTwoTone,
	MenuBookTwoTone,
	PaletteTwoTone,
	FlagTwoTone,
	StorageTwoTone,
	GavelTwoTone,
	QuizTwoTone,
	DashboardCustomizeTwoTone,
	ConfirmationNumberTwoTone,
	CorporateFareTwoTone,
	PersonTwoTone,
	HomeTwoTone,
	EmojiEventsTwoTone,
	HistoryTwoTone,
	GroupsTwoTone,
	ForwardToInboxTwoTone
	// Storefront
} from '@mui/icons-material';
import HistoryIcon from '@mui/icons-material/History';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { MenuItem } from '@aws-amplify/ui-react';
import Collapse from '@mui/material/Collapse';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RoleName, UserGroup } from '../../types/user';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	ListItemAvatar,
	Typography,
	styled,
	alpha,
	Chip,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Avatar
} from '@mui/material';
import ProfilePicture from '../ui/atoms/ProfilePicture';
import Logo from './Logo';
import ThemeSelector from './ThemeSelector';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import { Organisation, OrganisationType } from '../../types/organisation';
import { intersection } from 'lodash';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import { useSelector } from 'react-redux';
import { NotificationCategory } from '../../types/notification';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import {
	getActiveSubscriptions,
	listCandidatInterestsNotifications,
	listDocumentRequestNotificationCount,
	listVerificationRequestNotifications,
	listFamilyMemberNotifications
} from '../../redux/slices/organisation';
import { userLeavingOrganisation, readCurrentUser } from '../../redux/slices/user';
import { useBucketImage } from '../../hooks/useBucketImage';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useOrganisationGrants } from '../../hooks/useOrganisationGrants';
import { isOrganisationCheck } from '../../utils';
import { isFamilyOrganization } from '../../utils/organizationUtils';
import { Skeleton } from '@mui/material';
import { readConversation } from '../../redux/slices/ai-assistant';
import { listBulkUploadDocuments } from '../../redux/slices/document';
import { ORG_DOCUMENT_HISTORY_FLAG } from '../../constants/orgDocumentHistory';
import { AgentType } from '../../types/ai-assistant';
import AddCommentIcon from '@mui/icons-material/AddComment';
import NewBadge from './NewBadge';
import { dismissFeature, useAnyFeatureNew, useIsFeatureNew } from '../../hooks/useIsFeatureNew';
import { useRovingTabIndex } from '../../hooks/useRovingTabIndex';
import { PLATFORM_FEATURE_IDS } from '../../config/featureRegistry';
import { PARTNER_BRANDING_FLAG, hasPartnerBrandingFeature } from '../../constants/partnerBranding';
import {
	CONSTITUENTS_FEATURE,
	CUSTOM_EMAILS_FEATURE,
	PARTNER_PROGRAM_FLAG,
	hasFeatureGrant
} from '../../constants/partnerGrants';
import { CUSTOM_EMAILS_FLAG } from '../../constants/emailTemplates';
import AnaAvatar from '../../pages/Ana/AnaAvatar';

export type MenuItem = {
	label: string;
	icon?: React.ReactElement;
	children?: MenuItem[];
	actionRequired?: any;
	href?: string;
	helpDescription?: string;
	className?: string;
	/** Registry id used to surface a self-expiring "New" discovery badge. */
	featureId?: string;
};

const SidebarMenuItem = ({
	label,
	icon,
	children,
	href,
	helpDescription,
	actionRequired,
	className,
	featureId
}: MenuItem) => {
	return children?.length ? (
		<SidebarCollapseMenuItem {...{ label, icon, children, className }} />
	) : (
		<SidebarSimpleMenuItem {...{ label, icon, href, helpDescription, actionRequired, className, featureId }} />
	);
};

const SmallBadge = styled(Badge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		right: 0.5,
		top: 28,
		border: `-0.5px solid ${theme.palette.background.paper}`,
		padding: '0 4px'
	}
}));

/**
 * Shared glassmorphism shell for the sidebar accordions. Defined once so every
 * section stays in sync — previously this ~80-line block was pasted onto each
 * panel and had already drifted (the "Ana" panel was missing the noise layer).
 */
const SIDEBAR_NOISE_SVG =
	"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.035'/></svg>\")";

const GlassAccordion = styled(Accordion)(({ theme }) => ({
	position: 'relative',
	overflow: 'hidden',
	backgroundColor: 'transparent',
	backgroundImage: 'none',
	background: 'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.04))',
	backdropFilter: 'blur(26px) saturate(190%)',
	WebkitBackdropFilter: 'blur(26px) saturate(190%)',
	borderRadius: '20px',
	border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
	boxShadow: '0 30px 60px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.55)',
	marginBottom: theme.spacing(2),
	'&::before': { display: 'none' },
	'&::after': {
		content: '""',
		position: 'absolute',
		inset: 0,
		pointerEvents: 'none',
		backgroundImage: SIDEBAR_NOISE_SVG,
		mixBlendMode: 'overlay'
	},
	'&:hover': {
		boxShadow: `0 35px 80px ${alpha(theme.palette.primary.main, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.65)`
	}
}));

const GlassAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
	backgroundColor: 'transparent',
	borderRadius: '20px',
	paddingLeft: theme.spacing(2),
	paddingRight: theme.spacing(2),
	minHeight: 56,
	'&.Mui-expanded': { minHeight: 56 },
	'&:hover': { background: alpha(theme.palette.primary.main, 0.08) },
	'& .MuiAccordionSummary-content': {
		margin: 0,
		'&.Mui-expanded': { margin: 0 }
	},
	'& .MuiAccordionSummary-expandIconWrapper': {
		color: alpha(theme.palette.text.primary, 0.5),
		transition: theme.transitions.create(['transform', 'color'], { duration: 200 })
	},
	'&:hover .MuiAccordionSummary-expandIconWrapper': {
		color: theme.palette.primary.main
	}
}));

type SidebarSectionProps = {
	/** Stable id; drives the `${id}-header`/`${id}-content` aria wiring. */
	id: string;
	icon: React.ReactNode;
	title: string;
	/** Optional trailing badge next to the title (e.g. a "New" dot). */
	badge?: React.ReactNode;
	/** Optional help text; rendered as a non-interactive tooltip trigger. */
	help?: string;
	/** When true the icon is rendered without the tinted chip frame (e.g. a
	    self-contained mark like the Ana avatar orb). */
	iconBare?: boolean;
	defaultExpanded?: boolean;
	/** When provided, the section becomes controlled (single-open behaviour). */
	expanded?: boolean;
	onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
	children: React.ReactNode;
};

/** A single collapsible sidebar section using the shared glass shell. */
const SidebarSection = ({
	id,
	icon,
	title,
	badge,
	help,
	iconBare = false,
	defaultExpanded = true,
	expanded,
	onChange,
	children
}: SidebarSectionProps) => (
	<GlassAccordion {...(onChange ? { expanded, onChange } : { defaultExpanded })} disableGutters elevation={0}>
		<GlassAccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${id}-content`} id={`${id}-header`}>
			<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
					<Box
						sx={(theme) => ({
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
							width: 34,
							height: 34,
							mr: 1.5,
							overflow: 'hidden',
							borderRadius: iconBare ? '50%' : '11px',
							color: theme.palette.primary.main,
							background: iconBare
								? 'transparent'
								: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)}, ${alpha(
										theme.palette.primary.main,
										0.08
								  )})`,
							boxShadow: iconBare ? 'none' : `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`,
							'& > svg': { fontSize: '1.25rem' }
						})}
					>
						{icon}
					</Box>
					<Typography
						noWrap
						sx={{
							fontWeight: 700,
							fontSize: '0.8rem',
							letterSpacing: '0.08em',
							textTransform: 'uppercase',
							opacity: 0.85
						}}
					>
						{title}
					</Typography>
					{badge && <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>{badge}</Box>}
				</Box>
				{help && (
					<Tooltip title={help}>
						{/* Plain span — not a button — so we never nest an interactive
						    control inside the AccordionSummary's own button. */}
						<Box
							component="span"
							onClick={(e) => e.stopPropagation()}
							sx={{ display: 'flex', alignItems: 'center', color: 'action.active', ml: 1 }}
						>
							<HelpIcon fontSize="small" />
						</Box>
					</Tooltip>
				)}
			</Box>
		</GlassAccordionSummary>
		<AccordionDetails
			sx={(theme) => ({
				backgroundColor: 'transparent',
				px: 1,
				pt: 0.5,
				pb: 1,
				'& .MuiListItemButton-root': {
					position: 'relative',
					borderRadius: '12px',
					marginInline: theme.spacing(0.5),
					marginBlock: theme.spacing(0.25),
					transition: theme.transitions.create(['background-color', 'box-shadow'], { duration: 160 }),
					'&::before': {
						content: '""',
						position: 'absolute',
						left: 6,
						top: '50%',
						transform: 'translateY(-50%)',
						width: 3,
						height: 0,
						borderRadius: 3,
						backgroundColor: theme.palette.primary.main,
						transition: theme.transitions.create('height', { duration: 160 })
					},
					'&:hover': {
						backgroundColor: alpha(theme.palette.primary.main, 0.1),
						boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.22)}`
					},
					'&:hover::before': { height: 16 },
					'&.Mui-selected, &.active': {
						backgroundColor: alpha(theme.palette.primary.main, 0.18),
						boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
						'&::before': { height: 22 }
					}
				},
				'& .MuiListItemIcon-root': {
					minWidth: 38,
					color: alpha(theme.palette.text.primary, 0.7)
				},
				'& .MuiListItemText-primary': {
					fontSize: '0.9rem',
					fontWeight: 500,
					letterSpacing: '0.01em'
				}
			})}
		>
			{children}
		</AccordionDetails>
	</GlassAccordion>
);

/**
 * Whether the given menu href matches the current path. Trailing slashes are
 * normalised and we match the segment boundary so `/wallet` highlights for
 * `/wallet` and `/wallet/123` but never for `/wallet-something`.
 */
const isHrefActive = (pathname: string, href?: string) => {
	if (!href || href === '/') return false;
	const normalized = href.endsWith('/') ? href.slice(0, -1) : href;
	return pathname === normalized || pathname.startsWith(`${normalized}/`);
};

/**
 * Routes owned by the Platform section (PlatformDrawer). Used to expand the
 * correct top-level accordion on first load. Keep in sync with `platformMenu`.
 */
const PLATFORM_PATHS = [
	'/admin/partners',
	'/admin/school-directory',
	'/admin/ai-assistant',
	'/admin/support',
	'/admin/appearance',
	'/admin/feature-flags',
	'/admin/system',
	'/admin/legal',
	'/admin/email-templates',
	'/admin/faq'
];
const isPlatformPath = (pathname: string) => PLATFORM_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const SidebarSimpleMenuItem = ({
	label,
	icon,
	href,
	helpDescription,
	actionRequired,
	className,
	featureId
}: MenuItem) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	// Timestamps let a dark-shipped feature badge from the day its flag went on
	// rather than the day it was released.
	const enabledAt = useAppSelector((state) => state.featureFlags?.enabledAt);
	const isNew = useIsFeatureNew(featureId, enabledAt);
	const showHelpTooltips = useAppSelector((state) => state.featureFlags?.flags?.['sidebar-help-tooltips']) === true;
	const isActive = isHrefActive(pathname, href);
	return (
		<ListItemButton
			sx={{ paddingLeft: 2.5 }}
			selected={isActive}
			onClick={() => {
				if (featureId) {
					dismissFeature(featureId);
				}
				navigate(href ?? '/');
			}}
			className={className}
		>
			<ListItemIcon>{icon}</ListItemIcon>
			<ListItemText primary={label} />
			{isNew && (
				<Box sx={{ display: 'flex', alignItems: 'center', mr: '10px' }}>
					<NewBadge />
				</Box>
			)}
			{actionRequired && <Badge badgeContent={actionRequired} sx={{ mr: '10px' }} color="error"></Badge>}
			{showHelpTooltips && helpDescription && (
				<Tooltip title={helpDescription}>
					<IconButton>
						<HelpIcon></HelpIcon>
					</IconButton>
				</Tooltip>
			)}
		</ListItemButton>
	);
};

const SidebarCollapseMenuItem = ({ label, icon, children, className }: MenuItem) => {
	const { pathname } = useLocation();
	const childActive = !!children?.some((child) => isHrefActive(pathname, child.href));
	const [open, setOpen] = useState(childActive);
	// Keep the submenu expanded when navigating directly (or via back/forward) to an active child route.
	useEffect(() => {
		if (childActive) {
			setOpen(true);
		}
	}, [childActive]);
	const handleClick = () => {
		setOpen((current) => !current);
		window.dispatchEvent(new CustomEvent('MyJourneyAccordianOpened'));
	};

	return (
		<Box component="li" sx={{ listStyle: 'none' }}>
			<ListItemButton sx={{ paddingLeft: 2.5 }} onClick={handleClick} className={className}>
				<ListItemIcon>{icon}</ListItemIcon>
				<ListItemText primary={label} />
				<Box>{open ? <ExpandLess /> : <ExpandMore />}</Box>
			</ListItemButton>
			<Collapse in={open} timeout="auto" unmountOnExit>
				<List component="div" disablePadding>
					{children?.map((child) => (
						<SidebarMenuItem key={child.label} {...child} />
					))}
				</List>
			</Collapse>
		</Box>
	);
};

const OrganisationDrawer = ({ organisation, orgCount }: { organisation: Organisation; orgCount: number }) => {
	const { t } = useTranslation();
	const { getOrganisationRoles, groups, user } = useAuthenticatedUser();
	const { organisationGrants, getGrants } = useOrganisationGrants(organisation.id);
	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(listDocumentRequestNotificationCount(organisation.id));
		dispatch(listCandidatInterestsNotifications(organisation.id));
		dispatch(listVerificationRequestNotifications(organisation.id));
		dispatch(getActiveSubscriptions({ organisationId: organisation.id }));
		// Fetch family member notifications for family orgs
		if (isFamilyOrganization(organisation)) {
			dispatch(listFamilyMemberNotifications(organisation.id));
		}
		getGrants();
	}, [dispatch]);

	const roles = getOrganisationRoles(organisation.id);

	const [candidateInterestsNotifications, verificationRequestNotifications, sharedWalletRequestNotificationCount] =
		useSelector((state: RootState) => [
			state.organisation.candidate_interests_notifications,
			state.organisation.verification_requests_notifications,
			state.organisation.shared_wallet_requests_notification_count
		]);

	const familyMemberNotifications = useSelector((state: RootState) => state.organisation.family_member_notifications);
	const familyMemberNotificationCount = Object.keys(familyMemberNotifications ?? {}).length;

	const brandingEnabled = useAppSelector((state) => state.featureFlags?.flags?.[PARTNER_BRANDING_FLAG]) === true;
	// Default-off, matching the route guard: hiding the nav item alone would leave the URL
	// reachable, and showing it where the API is not deployed would link to a dead page.
	const documentHistoryEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.[ORG_DOCUMENT_HISTORY_FLAG]) === true;
	// Default-off: absent flags read as off, so an environment that has never heard of
	// this flag hides the entry rather than showing the wireframe. Paired with the
	// route guard in router/organisation.tsx — hiding the nav alone leaves the URL open.
	const partnerProgramEnabled = useAppSelector((state) => state.featureFlags?.flags?.[PARTNER_PROGRAM_FLAG]) === true;
	// Default-off, paired with the route guard in router/organisation.tsx.
	const customEmailsEnabled = useAppSelector((state) => state.featureFlags?.flags?.[CUSTOM_EMAILS_FLAG]) === true;

	let organisationActiveSubscriptionsCount: number;
	if (roles.includes(RoleName.BillingAdministrator)) {
		useEffect(() => {
			dispatch(getActiveSubscriptions({ organisationId: organisation.id }));
		}, [dispatch]);

		const organisationActiveSubscriptions = useSelector(
			(state: RootState) => state.organisation.organisationActiveSubscriptions
		);
		organisationActiveSubscriptionsCount =
			organisationActiveSubscriptions?.items?.filter(
				(subscription: { type: string; cancelDue: boolean }) =>
					subscription.type === 'license' && subscription.cancelDue
			).length || 0;
	}

	const dashboardAllowedRoleScope = intersection(roles, [
		'hr',
		'registrar',
		'admissionsOfficer',
		'verifier',
		'verifierOnlyOrg',
		'onboarding',
		'pearsanaAdmin'
	]);

	if (organisation.id === process.env.REACT_APP_PEARSANA_ID && roles.includes(RoleName.OrgnaisationAdmin)) {
		dashboardAllowedRoleScope.push('pearsanaAdmin');
	}

	/* eslint-disable no-mixed-spaces-and-tabs */
	const pearsanaMenu = [
		organisation.isPearsana && {
			label: t('pages.admin.prospective.sidebarTitle'),
			icon: <BadgeIcon />,
			href: `/organisations/${organisation.id}/prospective`
		},
		organisation.isPearsana && {
			label: t('pages.admin.logging.portal.title'),
			icon: <DescriptionIcon />,
			href: `/organisations/${organisation.id}/logging`
		}
	].filter(Boolean);

	const organisationMenu = useMemo(() => {
		return [
			{
				label:
					organisation.name ||
					(isFamilyOrganization(organisation)
						? t('pages.organisation.familyOrg.title', 'Family')
						: t('pages.organisation.details.title', 'Organisation')),
				icon: <ProfilePicture image={organisation.logo} sx={{ width: '1.5rem', height: '1.5rem' }} />,
				href: `/organisations/${organisation.id}/`,
				allowedRoles: [
					RoleName.OrgnaisationAdmin,
					RoleName.VerifierOnlyOrg,
					RoleName.BillingAdministrator,
					RoleName.FamilyOrganisationAdmin
				]
			},
			// Standard orgs: Billing Administrator retired — Account is the Settings › Plan &
			// billing tab, and Product Catalogue is reachable from there. Family orgs keep the
			// "Payments" entry (family dashboard not consolidated this pass). Pre-consolidation nav: see 1a52b5389.
			...(isFamilyOrganization(organisation)
				? [
						{
							label: t('pages.organisation.payments.title'),
							icon: (
								<Badge badgeContent={organisationActiveSubscriptionsCount} color="warning">
									<ManageAccountsIcon />
								</Badge>
							),
							children: [
								{
									label: t('pages.organisation.billingAdmin.accountStatus'),
									icon: (
										<Badge badgeContent={organisationActiveSubscriptionsCount} color="warning">
											<Diversity3TwoTone />
										</Badge>
									),
									href: `/organisations/${organisation.id}/billing-administrator/account-status`
								},
								{
									label: t('pages.organisation.billingAdmin.productCatalog'),
									icon: <StorefrontTwoTone />,
									href: `/organisations/${organisation.id}/billing-administrator/product-catalog`
								}
							],
							allowedRoles: [RoleName.BillingAdministrator, RoleName.FamilyOrganisationAdmin]
						}
				  ]
				: []),
			// Standard orgs: Staff retired — it's now Settings › Members (full invite + tables).
			// Family orgs keep "Family Members" (family dashboard not consolidated). Pre-consolidation nav: see 1a52b5389.
			...(isFamilyOrganization(organisation)
				? [
						{
							label: t('pages.organisation.familyMembers.title', 'Family Members'),
							icon: (
								<Badge badgeContent={familyMemberNotificationCount} color="info">
									<FamilyRestroomTwoTone />
								</Badge>
							),
							href: `/organisations/${organisation.id}/staff`,
							allowedRoles: [RoleName.FamilyOrganisationAdmin]
						}
				  ]
				: []),
			// Developer moved into the Settings hub (Developers tab). Route + page kept;
			// nav entry removed as part of the IA consolidation (pre-consolidation nav in 1a52b5389).
			// Dashboards: single Workspaces entry → hub with pill tabs (was an accordion at the
			// top of the drawer). Shown when the user has any role-scoped dashboard.
			...(dashboardAllowedRoleScope.length
				? [
						{
							label: t('pages.organisation.dashboard.title', 'Dashboards'),
							icon: <InsightsTwoTone />,
							href: `/organisations/${organisation.id}/dashboards`,
							allowedRoles: [RoleName.OrgnaisationAdmin],
							alwaysShow: true
						}
				  ]
				: []),
			...(hasFeatureGrant(organisationGrants?.features, CONSTITUENTS_FEATURE)
				? [
						{
							label: t('pages.organisation.constituents.title', 'Constituents'),
							icon: <GroupsTwoTone />,
							href: `/organisations/${organisation.id}/constituents`,
							allowedRoles: [RoleName.OrgnaisationAdmin]
						}
				  ]
				: []),
			...(documentHistoryEnabled
				? [
						{
							label: t('pages.organisation.documentHistory.title', 'Document History'),
							icon: <HistoryTwoTone />,
							href: `/organisations/${organisation.id}/document-history`,
							allowedRoles: [RoleName.OrgnaisationAdmin]
						}
				  ]
				: []),
			...(partnerProgramEnabled
				? [
						{
							label: t('pages.organisation.partner.title', 'Partner Program'),
							icon: <Diversity3TwoTone />,
							href: `/organisations/${organisation.id}/partner-program`,
							allowedRoles: [RoleName.OrgnaisationAdmin]
						}
				  ]
				: []),
			{
				// Organisation Settings hub (RFC: IA consolidation).
				label: t('pages.organisation.settings.title', 'Settings'),
				icon: <ManageAccountsIcon />,
				href: `/organisations/${organisation.id}/settings`,
				allowedRoles: [
					RoleName.OrgnaisationAdmin,
					RoleName.VerifierOnlyOrg,
					RoleName.BillingAdministrator,
					RoleName.FamilyOrganisationAdmin
				]
			},
			...(brandingEnabled && hasPartnerBrandingFeature(organisationGrants?.features)
				? [
						{
							label: t('pages.organisation.branding.title'),
							icon: <PaletteTwoTone />,
							href: `/organisations/${organisation.id}/branding`,
							allowedRoles: [RoleName.OrgnaisationAdmin]
						}
				  ]
				: []),
			...(customEmailsEnabled && hasFeatureGrant(organisationGrants?.features, CUSTOM_EMAILS_FEATURE)
				? [
						{
							label: t('pages.organisation.emailSettings.title'),
							icon: <ForwardToInboxTwoTone />,
							href: `/organisations/${organisation.id}/email-settings`,
							allowedRoles: [RoleName.OrgnaisationAdmin]
						}
				  ]
				: []),
			{
				label: t('pages.organisation.employees.title'),
				icon: <SupervisorAccountTwoTone />,
				href: `/organisations/${organisation.id}/employees`,
				allowedRoles: [
					RoleName.OrgnaisationAdmin,
					RoleName.HR,
					RoleName.Onboarding,
					RoleName.Verifier,
					RoleName.Agent
				],
				needHr: true,
				notForFamily: true // Hide employees menu for family orgs
			},
			{
				label: t('pages.organisation.students.title'),
				icon: <SchoolTwoTone />,
				href: `/organisations/${organisation.id}/students`,
				allowedEducational: [RoleName.OrgnaisationAdmin, RoleName.AdmissionsOfficer, RoleName.Registrar]
			},
			{
				label: t('pages.organisation.students.myStudents'),
				icon: <SchoolTwoTone />,
				href: `/organisations/${organisation.id}/agent-students`,
				allowedAgency: [RoleName.Agent]
			},
			{
				label: t('pages.organisation.students.title'),
				icon: <SchoolTwoTone />,
				href: `/organisations/${organisation.id}/students`,
				allowedAgency: [RoleName.OrgnaisationAdmin]
			},
			{
				label: t('pages.organisation.verifications.title'),
				icon: (
					<Badge badgeContent={verificationRequestNotifications} color="info">
						<VerifiedTwoTone />
					</Badge>
				),
				href: `/organisations/${organisation.id}/verifications`,
				allowedRoles: [RoleName.Registrar, RoleName.Verifier, RoleName.VerifierOnlyOrg]
			},

			...(organisationGrants?.features.includes('portal')
				? [
						{
							// Portal is now a single entry → hub with pill tabs (Detailed Profile /
							// Course Profiles / Search), replacing the inner accordion.
							label: t('pages.organisation.portal.title'),
							icon: <TravelExplore />,
							href: `/organisations/${organisation.id}/portal`,
							allowedRoles: [
								RoleName.OrgnaisationAdmin,
								RoleName.Onboarding,
								RoleName.AdmissionsOfficer,
								RoleName.Registrar,
								RoleName.HR
							]
						}
				  ]
				: []),
			{
				label: t('pages.organisation.enquiries.title'),
				icon: (
					<Badge badgeContent={candidateInterestsNotifications} color="info">
						<Diversity3TwoTone />
					</Badge>
				),
				href: `/organisations/${organisation.id}/candidate-enquiries`,
				allowedRoles: [RoleName.AdmissionsOfficer, RoleName.HR]
			},
			{
				label: t('pages.organisation.sharedDocumentRequests.title'),
				icon: (
					<Badge badgeContent={sharedWalletRequestNotificationCount} color="info">
						<ViewSidebarTwoTone />
					</Badge>
				),
				href: `/organisations/${organisation.id}/shared-documents-requests`,
				allowedRoles: [RoleName.HR, RoleName.AdmissionsOfficer, RoleName.Onboarding, RoleName.Agent]
			}
		].filter(({ allowedRoles, allowedAgency, allowedEducational, needHr, notForFamily, alwaysShow }) => {
			if (alwaysShow) return true;
			// Hide menu items marked as notForFamily when organization is a family org
			if (notForFamily && isFamilyOrganization(organisation)) return false;
			if (needHr && !organisation.grants?.hr) return false;
			if (allowedEducational && organisation.type === OrganisationType.EducationalInstitution)
				return intersection(allowedEducational, roles).length;
			if (allowedAgency && organisation.grants?.agents) return intersection(allowedAgency, roles).length;
			return intersection(allowedRoles, roles).length;
		});
	}, [
		getOrganisationRoles,
		organisation,
		verificationRequestNotifications,
		candidateInterestsNotifications,
		familyMemberNotificationCount,
		organisationGrants,
		brandingEnabled,
		customEmailsEnabled
	]);
	/* eslint-disable no-mixed-spaces-and-tabs */

	const firstTimeSetupCheck = !organisation.extendCredit && organisationGrants?.firstTimeSetup;

	// Section headers for the Organisation drawer (mirrors the grouped Platform nav).
	// Group is derived from each item's route so we don't have to tag every entry.
	const groupForItem = (item: {
		href?: string;
		children?: { href?: string }[];
	}): 'overview' | 'manage' | 'workspaces' => {
		const href = item.href ?? '';
		if (href === `/organisations/${organisation.id}/` || href === `/organisations/${organisation.id}`)
			return 'overview';
		const isBilling =
			href.includes('/billing-administrator') ||
			(item.children ?? []).some((c) => c.href?.includes('/billing-administrator'));
		if (isBilling || href.endsWith('/settings') || href.endsWith('/branding') || href.endsWith('/email-settings'))
			return 'manage';
		return 'workspaces';
	};
	// Workspaces ordered by the constituent lifecycle: acquire → convert → manage →
	// collect → verify, with the strategic Partner Program last.
	const workspaceRank = (item: { href?: string }): number => {
		const h = item.href ?? '';
		if (h.includes('/dashboards')) return 0;
		if (h.includes('/portal')) return 1;
		if (h.includes('/candidate-enquiries')) return 2;
		if (h.includes('/students') || h.includes('/agent-students') || h.includes('/employees')) return 3;
		if (h.includes('/shared-documents-requests')) return 4;
		if (h.includes('/verifications')) return 5;
		if (h.includes('/partner-program')) return 6;
		return 99;
	};
	const organisationGroups = [
		{ key: 'overview', title: '' },
		{ key: 'workspaces', title: 'Workspaces' },
		{ key: 'manage', title: 'Manage' }
	]
		.map((g) => {
			const items = organisationMenu.filter((it) => groupForItem(it) === g.key);
			return {
				...g,
				items: g.key === 'workspaces' ? [...items].sort((a, b) => workspaceRank(a) - workspaceRank(b)) : items
			};
		})
		.filter((g) => g.items.length);

	if (!organisation || !organisationMenu.length) return <></>;

	const menuContent = (
		<List
			sx={(theme) => ({
				py: 0,
				'& .MuiListItemButton-root': {
					position: 'relative',
					borderRadius: '12px',
					mx: 0.5,
					my: 0.3,
					'&:hover': {
						background: alpha(theme.palette.primary.main, 0.08),
						boxShadow: 'none'
					},
					'&.Mui-selected': {
						background: alpha(theme.palette.primary.main, 0.14),
						boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
						'&:hover': { background: alpha(theme.palette.primary.main, 0.18) }
					}
				},
				// Let long org / item names wrap to two lines instead of truncating mid-word.
				'& .MuiListItemText-primary': {
					whiteSpace: 'normal',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
					lineHeight: 1.25,
					fontSize: 14
				}
			})}
		>
			{orgCount > 1 && (
				<ListItemButton component={Link} to={`/organisations/${organisation.id}`}>
					<ListItemAvatar>
						<ProfilePicture image={organisation.logo} sx={{ width: '2rem', height: '2rem' }} />
					</ListItemAvatar>
					<ListItemText primary={organisation.name} />
				</ListItemButton>
			)}
			{/* Dashboards moved into the Workspaces group as a pill-tab hub (see organisationMenu). */}
			{(!isOrganisationCheck(groups, user) || !firstTimeSetupCheck) &&
				organisationGroups.map((g) => (
					<React.Fragment key={g.key}>
						{g.title && <Typography sx={PLATFORM_GROUP_LABEL_SX}>{g.title}</Typography>}
						{g.items.map((item) => (
							<SidebarMenuItem key={item.label} {...item} />
						))}
					</React.Fragment>
				))}
			{organisation.isPearsana === true &&
				pearsanaMenu.map((item, index) => {
					return item ? <SidebarMenuItem key={index} {...item} /> : null;
				})}
		</List>
	);

	// Single org: render flat menu directly inside parent accordion
	if (orgCount <= 1) {
		return menuContent;
	}

	// Multiple orgs: wrap each in its own collapsible sub-accordion
	return (
		<Accordion
			defaultExpanded={false}
			disableGutters
			elevation={0}
			sx={(theme) => ({
				backgroundColor: 'transparent',
				backgroundImage: 'none',
				background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(
					theme.palette.primary.main,
					0.02
				)})`,
				borderRadius: '16px !important',
				border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
				overflow: 'hidden',
				mb: 0.5,
				transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
				'&::before': { display: 'none' },
				'&:hover': {
					border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
					boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`
				},
				'&.Mui-expanded': {
					background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
						theme.palette.primary.main,
						0.03
					)})`,
					border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
					boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(
						'#fff',
						0.08
					)}`
				}
			})}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />}
				sx={(theme) => ({
					backgroundColor: 'transparent',
					borderRadius: '16px',
					px: 1.5,
					py: 0,
					minHeight: 48,
					'&.Mui-expanded': { minHeight: 48 },
					'&:hover': { background: alpha(theme.palette.primary.main, 0.06) },
					'& .MuiAccordionSummary-content': {
						margin: 0,
						alignItems: 'center',
						gap: 1,
						'&.Mui-expanded': { margin: 0 }
					}
				})}
			>
				<ProfilePicture image={organisation.logo} sx={{ width: '1.8rem', height: '1.8rem' }} />
				<Typography
					variant="body2"
					fontWeight={600}
					noWrap
					sx={{ fontSize: '0.85rem', flex: 1 }}
					title={organisation.name}
				>
					{organisation.name}
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ backgroundColor: 'transparent', px: 0.5, pt: 0, pb: 0.5 }}>
				{menuContent}
			</AccordionDetails>
		</Accordion>
	);
};
/** Roles that produce visible menu items in OrganisationDrawer */
const ADMIN_MENU_ROLES = [
	RoleName.BillingAdministrator,
	RoleName.FamilyOrganisationAdmin,
	RoleName.OrgnaisationAdmin,
	RoleName.VerifierOnlyOrg,
	RoleName.HR,
	RoleName.Onboarding,
	RoleName.Verifier,
	RoleName.Agent,
	RoleName.Registrar,
	RoleName.AdmissionsOfficer
];

const OrganisationMembershipCard = ({ organisation }: { organisation: Organisation }) => {
	const { t } = useTranslation();
	const { getOrganisationRoles, user } = useAuthenticatedUser();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
	const { src } = useBucketImage(organisation.logo);

	const roles = getOrganisationRoles(organisation.id);
	const displayRoles = roles.map((role) => t(`common.types.user.roles.${role}`));

	const handleLeave = useCallback(async () => {
		if (!user?.id) return;
		setLeaveDialogOpen(false);
		await dispatch(userLeavingOrganisation({ userId: user.id, organisationId: organisation.id }));
		dispatch(readCurrentUser());
	}, [dispatch, user?.id, organisation.id]);

	return (
		<>
			<Box
				sx={(theme) => ({
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					p: 1.5,
					mx: 0.5,
					my: 0.5,
					borderRadius: '16px',
					overflow: 'hidden',
					background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
						theme.palette.primary.main,
						0.02
					)})`,
					backdropFilter: 'blur(12px)',
					border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
					cursor: 'pointer',
					transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
					'&:hover': {
						background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(
							theme.palette.primary.main,
							0.06
						)})`,
						border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
						boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(
							'#fff',
							0.1
						)}`,
						transform: 'translateY(-1px)'
					},
					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						height: '1px',
						background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.25)}, transparent)`
					}
				})}
				onClick={() => navigate(`/account#org-${organisation.id}`)}
			>
				<Avatar
					src={src}
					sx={(theme) => {
						const accent =
							theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main;
						return {
							width: 34,
							height: 34,
							fontSize: '0.8rem',
							fontWeight: 700,
							bgcolor: alpha(accent, 0.2),
							color: accent,
							boxShadow: `0 2px 8px ${alpha(accent, 0.25)}`,
							border: `1.5px solid ${alpha(accent, 0.3)}`
						};
					}}
				>
					{organisation.name?.charAt(0)?.toUpperCase()}
				</Avatar>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Typography
							variant="body2"
							fontWeight={600}
							noWrap
							title={organisation.name}
							sx={{ fontSize: '0.82rem', letterSpacing: '0.01em' }}
						>
							{organisation.name}
						</Typography>
						<Chip
							label={
								isFamilyOrganization(organisation)
									? 'Family'
									: organisation.type === OrganisationType.EducationalInstitution
									? 'Edu'
									: 'Co'
							}
							size="small"
							sx={(theme) => {
								const accent =
									theme.palette.mode === 'dark'
										? theme.palette.primary.light
										: theme.palette.primary.main;
								return {
									height: 16,
									fontSize: '0.55rem',
									fontWeight: 700,
									letterSpacing: '0.05em',
									textTransform: 'uppercase',
									bgcolor: alpha(accent, 0.12),
									color: accent,
									border: 'none',
									'& .MuiChip-label': { px: 0.6 }
								};
							}}
						/>
					</Box>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
						{displayRoles.map((role) => (
							<Chip
								key={role}
								label={role}
								size="small"
								variant="outlined"
								sx={(theme) => {
									const accent =
										theme.palette.mode === 'dark'
											? theme.palette.primary.light
											: theme.palette.primary.main;
									return {
										height: 18,
										fontSize: '0.62rem',
										fontWeight: 500,
										borderColor: alpha(accent, 0.2),
										color: alpha(accent, 0.85),
										'& .MuiChip-label': { px: 0.5 }
									};
								}}
							/>
						))}
					</Box>
				</Box>
				<Tooltip title={t('pages.organisation.leave.title', 'Leave Organisation')}>
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							setLeaveDialogOpen(true);
						}}
						sx={(theme) => ({
							color: alpha(theme.palette.text.secondary, 0.5),
							transition: 'all 0.2s ease',
							'&:hover': {
								color: theme.palette.error.main,
								bgcolor: alpha(theme.palette.error.main, 0.08),
								transform: 'scale(1.1)'
							}
						})}
					>
						<LogoutIcon sx={{ fontSize: '0.95rem' }} />
					</IconButton>
				</Tooltip>
			</Box>

			<Dialog
				open={leaveDialogOpen}
				onClose={() => setLeaveDialogOpen(false)}
				PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
			>
				<DialogTitle>{t('pages.organisation.leave.title', 'Leave Organisation')}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t('pages.organisation.leave.confirm', `Are you sure you want to leave ${organisation.name}?`)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLeaveDialogOpen(false)}>{t('common.actions.cancel', 'Cancel')}</Button>
					<Button onClick={handleLeave} color="error" variant="contained">
						{t('pages.organisation.leave.action', 'Leave')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const AnaSidebarSection = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const user = useAppSelector((state) => state.user.currentUser);
	const recentConversations = useAppSelector((state) => state.aiAssistant.recentConversations.chatAgent);
	const location = window.location;

	useEffect(() => {
		if (user) {
			dispatch(readConversation({ userId: user.id, limit: 10, next: null, agentType: AgentType.CHAT_AGENT }));
		}
	}, [user, dispatch]);

	const conversations = [...(recentConversations?.items || [])]
		.sort(
			(a, b) =>
				new Date(b.lastMessageDateTime || b.creationDateTime).getTime() -
				new Date(a.lastMessageDateTime || a.creationDateTime).getTime()
		)
		.slice(0, 8);

	const currentConvId = new URLSearchParams(location.search).get('c');

	const handleNewConversation = () => {
		navigate('/ana');
	};

	const handleSelectConversation = (id: string) => {
		navigate(`/ana?c=${id}`);
	};

	return (
		<Box>
			<List dense disablePadding>
				<ListItemButton
					sx={{ py: 0.75, px: 2, borderRadius: 1 }}
					onClick={handleNewConversation}
					selected={location.pathname === '/ana' && !currentConvId}
				>
					<ListItemIcon sx={{ minWidth: 32 }}>
						<AddCommentIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={t('pages.aiAssistant.newConversation', 'New Conversation')}
						primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
					/>
				</ListItemButton>
			</List>
			{conversations.length > 0 && (
				<>
					<Divider sx={{ my: 0.75, mx: 2, opacity: 0.3 }} />
					<List dense disablePadding>
						{conversations.map((conv) => (
							<ListItemButton
								key={conv.id}
								sx={{ py: 0.4, px: 2, pl: 3.5, borderRadius: 1 }}
								selected={currentConvId === conv.id}
								onClick={() => handleSelectConversation(conv.id)}
							>
								<ListItemText
									primary={conv.title || 'Untitled'}
									primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { opacity: 0.85 } }}
								/>
							</ListItemButton>
						))}
					</List>
				</>
			)}
		</Box>
	);
};

const AdminDrawer = () => {
	const { t } = useTranslation();
	const advisorSupportEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['advisor-support-enabled']) !== false;
	const advertisementEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['advertisement-enabled']) === true;
	const resourceHubAdminEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['resource-hub-enabled']) !== false;
	const homepageEnabled = useAppSelector((state) => state.featureFlags?.flags?.['homepage-enabled']) === true;
	const shareTrackingEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['share-tracking-enabled']) === true;
	const orgDocumentHistoryEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.[ORG_DOCUMENT_HISTORY_FLAG]) === true;

	const adminMenu = useMemo(() => {
		return [
			{
				label: t('pages.admin.home.title'),
				icon: <HomeTwoTone />,
				href: '/admin/home',
				enabled: homepageEnabled
			},
			{
				label: t('pages.admin.digitalProducts.masterBiller.inviteClient.title'),
				icon: <AddBusinessIcon />,
				href: '/admin/masterbiller/invite'
			},
			{
				label: t('pages.admin.organisations.title'),
				icon: <BusinessTwoTone />,
				href: '/admin/organisations'
			},
			{
				label: t('pages.admin.digitalProducts.products.title'),
				icon: <InventoryIcon />,
				href: '/admin/digital-products'
			},
			{
				label: t('pages.admin.digitalCoupons.title'),
				icon: <DiscountTwoTone />,
				href: '/admin/digital-coupons'
			},
			{
				label: t('pages.admin.dashboard.title'),
				icon: <DashboardIcon />,
				href: '/admin/dashboards'
			},
			{
				label: t('pages.admin.organisations.orderHistory.title'),
				icon: <HistoryIcon />,
				href: '/admin/order-history'
			},
			{
				label: t('pages.admin.users.title'),
				icon: <AccountCircleTwoTone />,
				href: '/admin/users'
			},
			{
				label: t('pages.admin.sharingOverview.title', 'Document Sharing'),
				icon: <HistoryTwoTone />,
				href: '/admin/sharing-overview',
				enabled: shareTrackingEnabled
			},
			{
				label: t('pages.admin.documentHistory.title', 'Document History'),
				icon: <FolderSharedTwoTone />,
				href: '/admin/document-history',
				enabled: orgDocumentHistoryEnabled
			},
			{
				label: t('pages.admin.advertisement.title'),
				icon: <AdUnitsTwoTone />,
				href: '/admin/advertisement',
				enabled: advertisementEnabled
			},
			{
				label: t('pages.admin.advisorBookings.title'),
				icon: <SupportAgentTwoTone />,
				href: '/admin/advisor-bookings',
				enabled: advisorSupportEnabled
			},
			{
				label: t('pages.resourceHub.title', 'Resource Hub'),
				icon: <MenuBookTwoTone />,
				href: '/admin/resource-hub',
				enabled: resourceHubAdminEnabled
			}
		].filter((item) => item.enabled !== false);
	}, [advisorSupportEnabled, resourceHubAdminEnabled, advertisementEnabled, homepageEnabled, shareTrackingEnabled]);

	return (
		<>
			<Divider></Divider>
			<List
				sx={(theme) => ({
					'& .MuiListItemButton-root': {
						position: 'relative',
						borderRadius: '16px',
						mx: 0.5,
						my: 0.4,
						'&:hover': {
							background: alpha(theme.palette.primary.main, 0.12),
							boxShadow: `0 0 18px ${alpha(theme.palette.primary.main, 0.35)}`
						},
						'&.Mui-selected': {
							background: alpha(theme.palette.primary.main, 0.22),
							boxShadow: `
                      inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)},
                      0 12px 26px ${alpha(theme.palette.primary.main, 0.45)}
                    `
						}
					}
				})}
			>
				{adminMenu.map((item) => (
					<SidebarMenuItem key={item.label} {...item} />
				))}
			</List>
		</>
	);
};

const PLATFORM_GROUP_LABEL_SX = {
	ml: 1.5,
	mt: 1.5,
	mb: 0.5,
	fontSize: '0.68rem',
	fontWeight: 700,
	letterSpacing: '0.09em',
	textTransform: 'uppercase',
	color: 'text.secondary'
} as const;

/**
 * PROTOTYPE (ADR-010) — consolidated Platform drawer. Folds the app-admin adminMenu
 * ("Organisation" accordion) into one grouped Platform area (Home / Operations /
 * Strategic / Tools / Settings). Rendered only when the `platform-nav-v2` flag is on;
 * flag off = current PlatformDrawer + AdminDrawer, so no regression.
 */
const PlatformDrawerV2 = () => {
	const { t } = useTranslation();

	const advisorSupportEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['advisor-support-enabled']) !== false;
	const advertisementEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['advertisement-enabled']) === true;
	const resourceHubAdminEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['resource-hub-enabled']) !== false;
	const homepageEnabled = useAppSelector((state) => state.featureFlags?.flags?.['homepage-enabled']) === true;
	const shareTrackingEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.['share-tracking-enabled']) === true;
	const orgDocumentHistoryEnabled =
		useAppSelector((state) => state.featureFlags?.flags?.[ORG_DOCUMENT_HISTORY_FLAG]) === true;
	const customEmailsEnabled = useAppSelector((state) => state.featureFlags?.flags?.[CUSTOM_EMAILS_FLAG]) === true;

	const groups = useMemo(
		() =>
			[
				{
					key: 'home',
					title: 'Overview',
					items: [
						{ label: t('pages.admin.dashboard.title'), icon: <DashboardIcon />, href: '/admin/dashboards' }
					]
				},
				{
					key: 'operations',
					title: 'Operations',
					// Ordered by concern: org lifecycle → people → commerce → advisory → monitoring.
					items: [
						{
							label: t('pages.admin.organisations.title'),
							icon: <BusinessTwoTone />,
							href: '/admin/organisations'
						},
						{
							label: t('pages.admin.digitalProducts.masterBiller.inviteClient.title'),
							icon: <AddBusinessIcon />,
							href: '/admin/masterbiller/invite'
						},
						{ label: t('pages.admin.users.title'), icon: <AccountCircleTwoTone />, href: '/admin/users' },
						{
							label: t('pages.admin.digitalProducts.products.title'),
							icon: <InventoryIcon />,
							href: '/admin/digital-products'
						},
						{
							label: t('pages.admin.digitalCoupons.title'),
							icon: <DiscountTwoTone />,
							href: '/admin/digital-coupons'
						},
						{
							label: t('pages.admin.organisations.orderHistory.title'),
							icon: <HistoryIcon />,
							href: '/admin/order-history'
						},
						{
							label: t('pages.admin.advisorBookings.title'),
							icon: <SupportAgentTwoTone />,
							href: '/admin/advisor-bookings',
							enabled: advisorSupportEnabled
						},
						{
							label: t('pages.admin.sharingOverview.title', 'Document Sharing'),
							icon: <HistoryTwoTone />,
							href: '/admin/sharing-overview',
							enabled: shareTrackingEnabled
						},
						{
							label: t('pages.admin.documentHistory.title', 'Document History'),
							icon: <FolderSharedTwoTone />,
							href: '/admin/document-history',
							enabled: orgDocumentHistoryEnabled
						},
						{
							label: t('pages.admin.advertisement.title'),
							icon: <AdUnitsTwoTone />,
							href: '/admin/advertisement',
							enabled: advertisementEnabled
						}
					]
				},
				{
					key: 'strategic',
					title: 'Strategic',
					items: [
						{
							label: t('pages.admin.partners.title', 'Partners'),
							icon: <Diversity3TwoTone />,
							href: '/admin/partners'
						}
					]
				},
				{
					key: 'data',
					title: 'Data',
					items: [
						{
							label: t('pages.admin.schoolDirectory.title', 'School Directory'),
							icon: <SchoolTwoTone />,
							href: '/admin/school-directory'
						}
					]
				},
				{
					key: 'tools',
					title: 'Tools',
					items: [
						{
							label: t('pages.aiAssistant.title'),
							icon: <AnaAvatar size={20} />,
							href: '/admin/ai-assistant',
							featureId: 'aiAssistant'
						},
						{
							label: t('pages.admin.support.title'),
							icon: <ConfirmationNumberTwoTone />,
							href: '/admin/support',
							featureId: 'caseManagement'
						}
					]
				},
				{
					key: 'settings',
					title: 'Settings',
					items: [
						{
							label: t('pages.admin.appearance.title'),
							icon: <PaletteTwoTone />,
							href: '/admin/appearance',
							featureId: 'appearance'
						},
						{
							label: t('pages.admin.featureFlags.title'),
							icon: <FlagTwoTone />,
							href: '/admin/feature-flags',
							featureId: 'featureFlags'
						},
						{
							label: t('pages.admin.system.title'),
							icon: <StorageTwoTone />,
							href: '/admin/system',
							featureId: 'system'
						},
						{
							// /admin/home edits the members' wallet homepage — content config, not an admin home.
							label: t('pages.admin.homepage.navLabel', 'Member Homepage'),
							icon: <HomeTwoTone />,
							href: '/admin/home',
							enabled: homepageEnabled
						},
						{
							label: t('pages.admin.legal.title', 'Legal Documents'),
							icon: <GavelTwoTone />,
							href: '/admin/legal',
							featureId: 'legal'
						},
						{
							label: t('pages.admin.emailTemplates.title', 'Email Templates'),
							icon: <ForwardToInboxTwoTone />,
							href: '/admin/email-templates',
							featureId: 'emailTemplates',
							enabled: customEmailsEnabled
						},
						{
							label: t('pages.admin.faqBuilder.title', 'FAQ Builder'),
							icon: <QuizTwoTone />,
							href: '/admin/faq',
							featureId: 'faqBuilder'
						},
						{
							label: t('pages.resourceHub.title', 'Resource Hub'),
							icon: <MenuBookTwoTone />,
							href: '/admin/resource-hub',
							enabled: resourceHubAdminEnabled
						}
					]
				}
			]
				.map((g) => ({ ...g, items: g.items.filter((i) => (i as { enabled?: boolean }).enabled !== false) }))
				.filter((g) => g.items.length),
		[
			t,
			homepageEnabled,
			advisorSupportEnabled,
			shareTrackingEnabled,
			advertisementEnabled,
			resourceHubAdminEnabled,
			customEmailsEnabled
		]
	);

	return (
		<List
			sx={(theme) => ({
				'& .MuiListItemButton-root': {
					borderRadius: '12px',
					mx: 0.5,
					my: 0.3,
					'&:hover': { background: alpha(theme.palette.primary.main, 0.08) },
					'&.Mui-selected': {
						background: alpha(theme.palette.primary.main, 0.14),
						boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`
					}
				}
			})}
		>
			{groups.map((g) => (
				<React.Fragment key={g.key}>
					<Typography sx={PLATFORM_GROUP_LABEL_SX}>{g.title}</Typography>
					{g.items.map((item) => (
						<SidebarMenuItem key={item.label} {...item} />
					))}
				</React.Fragment>
			))}
		</List>
	);
};

const PlatformDrawer = () => {
	const { t } = useTranslation();

	const triageEnabled = useAppSelector((state) => state.featureFlags?.flags?.['triage-enabled']) === true;
	const customEmailsEnabled = useAppSelector((state) => state.featureFlags?.flags?.[CUSTOM_EMAILS_FLAG]) === true;

	const platformMenu = useMemo(() => {
		return [
			{
				group: 'strategic',
				label: t('pages.admin.partners.title', 'Partners'),
				icon: <Diversity3TwoTone />,
				href: '/admin/partners'
			},
			{
				group: 'data',
				label: t('pages.admin.schoolDirectory.title', 'School Directory'),
				icon: <SchoolTwoTone />,
				href: '/admin/school-directory'
			},
			{
				group: 'tools',
				label: t('pages.aiAssistant.title'),
				icon: <AnaAvatar size={20} />,
				href: '/admin/ai-assistant',
				featureId: 'aiAssistant'
			},
			{
				group: 'tools',
				label: t('pages.admin.support.title'),
				icon: <ConfirmationNumberTwoTone />,
				href: '/admin/support',
				featureId: 'caseManagement'
			},
			{
				group: 'config',
				label: t('pages.admin.appearance.title'),
				icon: <PaletteTwoTone />,
				href: '/admin/appearance',
				featureId: 'appearance'
			},
			{
				group: 'config',
				label: t('pages.admin.featureFlags.title'),
				icon: <FlagTwoTone />,
				href: '/admin/feature-flags',
				featureId: 'featureFlags'
			},
			{
				group: 'config',
				label: t('pages.admin.system.title'),
				icon: <StorageTwoTone />,
				href: '/admin/system',
				featureId: 'system'
			},
			{
				group: 'config',
				label: t('pages.admin.legal.title', 'Legal Documents'),
				icon: <GavelTwoTone />,
				href: '/admin/legal',
				featureId: 'legal'
			},
			...(customEmailsEnabled
				? [
						{
							group: 'config',
							label: t('pages.admin.emailTemplates.title', 'Email Templates'),
							icon: <ForwardToInboxTwoTone />,
							href: '/admin/email-templates',
							featureId: 'emailTemplates'
						}
				  ]
				: []),
			{
				group: 'config',
				label: t('pages.admin.faqBuilder.title', 'FAQ Builder'),
				icon: <QuizTwoTone />,
				href: '/admin/faq',
				featureId: 'faqBuilder'
			}
		];
	}, [t, triageEnabled, customEmailsEnabled]);

	return (
		<List
			sx={(theme) => ({
				'& .MuiListItemButton-root': {
					position: 'relative',
					borderRadius: '16px',
					mx: 0.5,
					my: 0.4,
					'&:hover': {
						background: alpha(theme.palette.primary.main, 0.12),
						boxShadow: `0 0 18px ${alpha(theme.palette.primary.main, 0.35)}`
					},
					'&.Mui-selected': {
						background: alpha(theme.palette.primary.main, 0.22),
						boxShadow: `
                      inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)},
                      0 12px 26px ${alpha(theme.palette.primary.main, 0.45)}
                    `
					}
				}
			})}
		>
			{platformMenu.map((item, index) => (
				<React.Fragment key={item.label}>
					{index > 0 && item.group !== platformMenu[index - 1].group && (
						<Divider
							sx={(theme) => ({ my: 1, mx: 1.5, borderColor: alpha(theme.palette.primary.main, 0.15) })}
						/>
					)}
					<SidebarMenuItem {...item} />
				</React.Fragment>
			))}
		</List>
	);
};

const SidebarMenuSkeleton = ({ logoHeight }: { logoHeight?: string }) => {
	return (
		<Box sx={{ overflowX: 'hidden' }}>
			{/* Logo/Banner Skeleton */}
			<Box overflow={'hidden'} width={'100%'} px={1} height={logoHeight ?? '5rem'}>
				<Skeleton variant="rectangular" width="100%" height="100%" />
			</Box>

			{/* Personal Section Skeleton */}
			<Box sx={{ mt: 10 }}>
				<Box sx={{ px: 2, mb: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
						<Skeleton variant="circular" width={24} height={24} sx={{ mr: 3 }} />
						<Skeleton variant="text" width={120} height={32} />
					</Box>

					{/* Menu Items */}
					{[1, 2, 3].map((i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1, pl: 2 }}>
							<Skeleton variant="circular" width={20} height={20} sx={{ mr: 3 }} />
							<Skeleton variant="text" width={150} height={24} />
						</Box>
					))}
				</Box>

				{/* Organisation Section Skeleton */}
				<Box sx={{ px: 2, mt: '50%' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
						<Skeleton variant="circular" width={24} height={24} sx={{ mr: 3 }} />
						<Skeleton variant="text" width={140} height={32} />
					</Box>

					{/* Organisation Menu Items */}
					{[1, 2, 3, 4].map((i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1, pl: 2 }}>
							<Skeleton variant="circular" width={20} height={20} sx={{ mr: 3 }} />
							<Skeleton variant="text" width={160} height={24} />
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

const SidebarMenu = ({ closed, logoHeight }: { closed?: boolean; logoHeight?: string }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	// Make the whole sidebar a SINGLE Tab stop: Tab enters once, arrow keys move
	// between sections/items, Tab leaves straight to the page content.
	const { containerRef, rovingProps } = useRovingTabIndex<HTMLDivElement>();
	const featureFlagsLoading = useAppSelector((state) => state.featureFlags?.loading);
	const platformSettingsLoading = useAppSelector((state) => state.platformSettings?.loading);
	const platformHasNew = useAnyFeatureNew(PLATFORM_FEATURE_IDS);
	const [
		sharedwalletCount,
		verificationCount,
		sharedwalletExpiringCount,
		organisationBanner,
		organisationLogo,
		bannerBackground
	] = useSelector((state: RootState) => [
		state.notification.byCategory[NotificationCategory.SharedWallet]?.length,
		state.notification.byCategory[NotificationCategory.Verification]?.length,
		state.notification.shared_wallet,
		state.organisation.current?.banner,
		state.organisation.current?.logo,
		state.organisation.current?.bannerBackground
	]);
	const userGrant = useAppSelector((state) => state.user?.userGrantsList);
	const featureFlags = useAppSelector((state) => state.featureFlags?.flags);
	const showHelpTooltips = featureFlags?.['sidebar-help-tooltips'] === true;

	const advisorSupportEnabled = featureFlags?.['advisor-support-enabled'] !== false;
	const resourceHubEnabled = featureFlags?.['resource-hub-enabled'] !== false;
	const myJourneyEnabled = featureFlags?.['my-journey-enabled'] === true;
	const verificationsEnabled = featureFlags?.['verifications-enabled'] === true;
	const sharedWalletEnabled = featureFlags?.['shared-wallet-enabled'] === true;
	const conversationScreenEnabled = featureFlags?.['conversation-screen-enabled'] === true;
	const homepageEnabled = featureFlags?.['homepage-enabled'] === true;
	const shareTrackingEnabled = featureFlags?.['share-tracking-enabled'] === true;

	const { src: bannerSrc } = useBucketImage(organisationBanner);
	const { src: logoSrc } = useBucketImage(organisationLogo);

	const { groups, user, roles } = useAuthenticatedUser();

	/*
	 * Documents an organisation has sent that still need an accept or reject.
	 *
	 * Counted from the documents themselves rather than from unread notifications, and the
	 * distinction matters: the notification is an event ("this arrived", dismissible), the
	 * badge is a state ("this still needs you"). Driving the badge off unread notices would
	 * let a document be forgotten the moment its notice was read.
	 *
	 * Fetched here rather than only on the wallet page, which is where it was fetched before:
	 * a badge that appears only once you have opened the wallet cannot tell you there is
	 * something waiting inside it.
	 */
	const dispatch = useAppDispatch();
	useEffect(() => {
		dispatch(listBulkUploadDocuments());
	}, [dispatch]);

	const pendingIncomingDocumentCount = useSelector(
		(state: RootState) =>
			// Optional on the slice itself, not just the field: the sidebar renders in
			// contexts with a partial store, and a badge is never worth failing the whole
			// navigation over.
			(state.document?.userBulkUploadDocuments ?? []).filter((doc) => doc.bulkUploadStatus === 'pending').length
	);

	const baseMenu = useMemo(() => {
		return {
			title: 'Private',
			links: [
				...(homepageEnabled
					? [
							{
								label: t('pages.homepage.title'),
								icon: <HomeTwoTone />,
								href: '/?home=1',
								helpDescription: t('pages.homepage.help'),
								notAllowedRoles: [],
								className: 'home-menu-item'
							}
					  ]
					: []),
				{
					label: t('pages.wallet.title'),
					icon: <WalletTwoTone />,
					href: '/wallet/',
					helpDescription: t('pages.wallet.help'),
					notAllowedRoles: [],
					className: 'wallet-menu-item',
					actionRequired: pendingIncomingDocumentCount || undefined
				},
				{
					label: t('pages.wallet.sharedReports.title'),
					icon: <HistoryTwoTone />,
					href: '/wallet/shared-reports',
					helpDescription: t('pages.wallet.sharedReports.help'),
					notAllowedRoles: [],
					className: 'shared-reports-menu-item',
					enabled: shareTrackingEnabled
				},
				{
					label: t('pages.badges.title'),
					icon: <EmojiEventsTwoTone />,
					href: '/badges',
					helpDescription: t('pages.badges.help'),
					notAllowedRoles: [],
					className: 'badges-menu-item'
				},
				{
					label: t('pages.verifications.title'),
					icon: (
						<Badge badgeContent={verificationCount} color="info">
							<VerifiedTwoTone />
						</Badge>
					),
					href: '/verifications/',
					helpDescription: t('pages.verifications.help'),
					notAllowedRoles: [],
					className: 'verification-menu-item',
					enabled: verificationsEnabled
				}
			].filter(({ notAllowedRoles, enabled }) => {
				if (enabled === false) return false;
				return !intersection(notAllowedRoles, roles).length;
			})
		};
	}, [
		sharedwalletCount,
		verificationCount,
		sharedwalletExpiringCount,
		verificationsEnabled,
		homepageEnabled,
		shareTrackingEnabled
	]);

	const personalMenu = [
		baseMenu,
		{
			title: null,
			links: [
				{
					label: 'My Journey',
					icon: <RouteTwoTone />,
					className: 'my-journey-menu-item',
					children: [
						{
							label: t('pages.journey.title'),
							href: '/journey/my-profiles',
							className: 'journey-profile-menu-item'
						},
						{
							label: t('pages.journey.portal.title'),
							href: '/journey/portal',
							className: 'portal-menu-item'
						},
						{
							label: t('pages.journey.enquiries.title'),
							href: '/journey/my-enquiries',
							className: 'my-enquiries-menu-item'
						}
					],
					notAllowedRoles: [],
					enabled: myJourneyEnabled
				}
			].filter(({ notAllowedRoles, enabled }) => {
				if (enabled === false) return false;
				return !intersection(notAllowedRoles, roles).length;
			})
		},
		{
			title: 'Public',
			links: [
				{
					label: t('pages.shared.title'),
					icon: (
						<Badge badgeContent={sharedwalletCount} color="info">
							<SmallBadge badgeContent={sharedwalletExpiringCount} color="error">
								<FolderSharedTwoTone />
							</SmallBadge>
						</Badge>
					),
					href: '/shared-wallet/',
					className: 'shared-wallet-menu-item',
					helpDescription: t('pages.shared.help'),
					notAllowedRoles: [],
					enabled: sharedWalletEnabled
				},
				{
					label: t('pages.advisorSupport.title'),
					icon: <SupportAgentTwoTone />,
					href: '/advisor-support/',
					className: 'advisor-support-menu-item',
					helpDescription: t('pages.advisorSupport.help'),
					notAllowedRoles: [] as string[],
					enabled: advisorSupportEnabled
				},
				{
					label: t('pages.resourceHub.title', 'Resource Hub'),
					icon: <MenuBookTwoTone />,
					href: '/resource-hub/',
					className: 'resource-hub-menu-item',
					helpDescription: t('pages.resourceHub.help', 'Guides, templates, and recordings'),
					notAllowedRoles: [] as string[],
					enabled: resourceHubEnabled
				}
			].filter(({ notAllowedRoles, enabled }) => {
				if (enabled === false) return false;
				return !intersection(notAllowedRoles, roles).length;
			})
		}
	];

	const hidePersonalMenu =
		userGrant &&
		(userGrant?.personal?.onboarding === false || userGrant?.personal?.onboarding === null) &&
		user?.roles.length === 0;

	const hideOrganisationDrawer = () => {
		if (groups.includes(UserGroup.Admin)) return true;
		if (
			user?.roles?.every(
				(r) =>
					r.roleName === RoleName.Student ||
					r.roleName === RoleName.Employee ||
					r.roleName === RoleName.FamilyMember
			) ??
			true
		)
			return false;
		return true;
	};

	// Orgs where user has only basic roles (student/employee/familyMember) and no admin menu
	const membershipOnlyOrgs = useMemo(() => {
		if (!user?.organisations || !user?.roles) return [];
		return user.organisations.filter((org) => {
			const orgRoles = user.roles.filter((r) => r.organisationId === org.id).map((r) => r.roleName);
			// Show card if user has roles in this org but none that produce admin menu items
			return orgRoles.length > 0 && intersection(orgRoles, ADMIN_MENU_ROLES).length === 0;
		});
	}, [user?.organisations, user?.roles]);

	// Single-open accordion: only one top-level section is expanded at a time.
	// On first load, open the section that owns the current route — Platform for
	// PlatformDrawer routes (e.g. /admin/partners), otherwise Organisation when
	// shown, else Personal.
	const { pathname: activePathname } = useLocation();
	// ADR-010 prototype: consolidated Platform nav (folds admin "Organisation" menu in).
	// Visible by default so it can be reviewed; set the `platform-nav-v2` flag to false to
	// fall back to the old split nav, or restore the version in 1a52b5389.
	const platformNavV2 = useAppSelector((state) => state.featureFlags?.flags?.['platform-nav-v2']) !== false;
	const [expandedSection, setExpandedSection] = useState<string | false>(() => {
		if (isPlatformPath(activePathname)) return 'panel-platform';
		return hideOrganisationDrawer() ? 'panel2' : 'panel1';
	});
	const handleSectionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpandedSection(isExpanded ? panel : false);
	};

	const isLoading = featureFlagsLoading || platformSettingsLoading || !user;

	if (isLoading) {
		return <SidebarMenuSkeleton logoHeight={logoHeight} />;
	}

	return (
		<Box
			ref={containerRef}
			{...rovingProps}
			role="navigation"
			aria-label={t('common.sidebarNavigation', 'Sidebar navigation')}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflowX: 'hidden',
				// Keyboard focus ring for arrow-key navigation. MUI ButtonBase ships no
				// visible ring; an inset box-shadow follows the rounded item shapes and
				// isn't clipped by the accordions' `overflow: hidden`.
				'& .MuiButtonBase-root:focus-visible': {
					outline: 'none',
					boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`
				}
			}}
		>
			<Box
				overflow={'hidden'}
				width={'100%'}
				px={1}
				height={logoHeight ?? '5rem'}
				onClick={() => navigate('/')}
				sx={{
					display: 'flex',
					flexShrink: 0,
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'pointer',
					backgroundColor:
						!closed && organisationBanner && bannerBackground ? bannerBackground : 'transparent'
				}}
			>
				{closed ? (
					logoSrc ? (
						<img
							src={logoSrc}
							alt="Logo"
							style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
						/>
					) : (
						<Logo collapsed={closed} />
					)
				) : organisationBanner ? (
					<img src={bannerSrc} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
				) : (
					<Logo collapsed={closed} />
				)}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
				{(!hidePersonalMenu || groups.includes(UserGroup.Admin)) && !closed ? (
					<Box>
						{groups.includes(UserGroup.Admin) && (
							<SidebarSection
								id="panel-platform"
								icon={<DashboardCustomizeTwoTone />}
								title={t('common.sections.platform', 'Platform')}
								badge={platformHasNew ? <NewBadge dot /> : undefined}
								expanded={expandedSection === 'panel-platform'}
								onChange={handleSectionChange('panel-platform')}
							>
								{platformNavV2 ? <PlatformDrawerV2 /> : <PlatformDrawer />}
							</SidebarSection>
						)}

						{hideOrganisationDrawer() &&
							!(platformNavV2 && groups.includes(UserGroup.Admin) && !user?.organisations?.length) && (
								<SidebarSection
									id="panel2"
									icon={<CorporateFareTwoTone />}
									title={t('common.sections.organisation', 'Organisation')}
									help={showHelpTooltips ? t('common.types.help.organisation') : undefined}
									expanded={expandedSection === 'panel2'}
									onChange={handleSectionChange('panel2')}
								>
									{groups.includes(UserGroup.Admin) && !platformNavV2 && <AdminDrawer />}
									{user?.organisations.map((organisation) => (
										<OrganisationDrawer
											key={organisation.id}
											organisation={organisation}
											orgCount={user.organisations.length}
										/>
									))}
								</SidebarSection>
							)}

						{conversationScreenEnabled && (
							<SidebarSection
								id="panel-ana"
								icon={<AnaAvatar size={22} animated />}
								title="Ana"
								expanded={expandedSection === 'panel-ana'}
								onChange={handleSectionChange('panel-ana')}
							>
								<AnaSidebarSection />
							</SidebarSection>
						)}

						<SidebarSection
							id="panel1"
							icon={<PersonTwoTone />}
							title={t('common.sections.personal', 'Personal')}
							help={showHelpTooltips ? t('common.types.help.personal') : undefined}
							expanded={expandedSection === 'panel1'}
							onChange={handleSectionChange('panel1')}
						>
							<List component="nav" aria-labelledby="nested-list-subheader" disablePadding>
								{personalMenu
									.filter((group) => group.links.length > 0)
									.map((item, idx) => (
										<Box key={item.title ?? `group-${idx}`}>
											{item.title && (
												<Typography
													sx={{
														ml: 1.5,
														mt: idx === 0 ? 0.5 : 1.5,
														mb: 0.5,
														fontSize: '0.68rem',
														fontWeight: 700,
														letterSpacing: '0.09em',
														textTransform: 'uppercase',
														color: 'text.secondary'
													}}
												>
													{item.title}
												</Typography>
											)}
											{item.links.map((link) => (
												<SidebarMenuItem key={link.label} {...link} />
											))}
										</Box>
									))}
								{membershipOnlyOrgs.length > 0 && (
									<Box sx={{ mt: 1.5 }}>
										<Typography
											sx={{
												ml: 1.5,
												mb: 0.5,
												fontSize: '0.68rem',
												fontWeight: 700,
												letterSpacing: '0.09em',
												textTransform: 'uppercase',
												color: 'text.secondary'
											}}
										>
											{t('common.myOrganisations', 'My Organisations')}
										</Typography>
										{membershipOnlyOrgs.map((org) => (
											<OrganisationMembershipCard key={org.id} organisation={org} />
										))}
									</Box>
								)}
							</List>
						</SidebarSection>
					</Box>
				) : (
					<Box>
						{!hidePersonalMenu && (
							<>
								<Divider></Divider>
								<List sx={{ width: '100%' }} component="nav" aria-labelledby="nested-list-subheader">
									{baseMenu.links.map((item) => (
										<SidebarMenuItem key={item.label} {...item} />
									))}
								</List>
							</>
						)}
						{groups.includes(UserGroup.Admin) && <AdminDrawer />}
						{user?.organisations.map((organisation) => (
							<OrganisationDrawer
								key={organisation.id}
								organisation={organisation}
								orgCount={user.organisations.length}
							/>
						))}
					</Box>
				)}
			</Box>
			<Box sx={{ flexShrink: 0 }}>
				<Divider sx={{ my: 1 }} />
				<ThemeSelector collapsed={closed} />
			</Box>
		</Box>
	);
};

export default SidebarMenu;
